import { Router } from "express";

import { leaveAppealController } from "../controllers/leave-appeal-controller";
import { leaveController } from "../controllers/leave-controller";
import { authenticate, createRateLimit } from "../middleware/auth-middleware";
import { requireRole } from "../middleware/role-middleware";
import { uploadMiddleware } from "../middleware/upload-middleware";

const router = Router();

// Environment-aware rate limiting
const generalRateLimit = createRateLimit.general();

/**
 * Student Leave Request Routes
 */

/**
 * @route   POST /api/v1/student/leave-requests
 * @desc    Create a leave request for a single day (multipart: date, reason, documents)
 * @access  Private (Student)
 */
router.post(
  "/student/leave-requests",
  authenticate,
  requireRole(["student"]),
  generalRateLimit,
  // Accept up to 10 documents (PDF/Word/images) for leave evidence
  uploadMiddleware.array("documents", 10),
  (req, res) => leaveController.createLeaveRequest(req, res),
);

/**
 * @route   GET /api/v1/student/leave-requests
 * @desc    Get all leave requests for a student
 * @access  Private (Student)
 */
router.get(
  "/student/leave-requests",
  authenticate,
  requireRole(["student"]),
  generalRateLimit,
  (req, res) => leaveController.getStudentLeaveRequests(req, res),
);

/**
 * @route   PUT /api/v1/student/leave-requests/:id/cancel
 * @desc    Cancel a leave request
 * @access  Private (Student)
 */
router.put(
  "/student/leave-requests/:id/cancel",
  authenticate,
  requireRole(["student"]),
  generalRateLimit,
  (req, res) => leaveController.cancelLeaveRequest(req, res),
);

/**
 * @route   POST /api/v1/student/leave-requests/:id/appeal
 * @desc    Submit appeal for rejected leave request
 * @access  Private (Student)
 */
router.post(
  "/student/leave-requests/:id/appeal",
  authenticate,
  requireRole(["student"]),
  generalRateLimit,
  uploadMiddleware.array("documents", 10),
  (req, res) => leaveAppealController.createLeaveAppeal(req, res),
);

/**
 * Teacher Leave Request Routes
 */

router.get(
  "/teacher/leave-requests",
  authenticate,
  requireRole(["teacher"]),
  generalRateLimit,
  (req, res) => leaveController.getTeacherLeaveRequests(req, res),
);

router.get(
  "/teacher/leave-requests/:id",
  authenticate,
  requireRole(["teacher"]),
  generalRateLimit,
  (req, res) => leaveAppealController.getTeacherLeaveRequestById(req, res),
);

router.put(
  "/teacher/leave-requests/:id/approve",
  authenticate,
  requireRole(["teacher"]),
  generalRateLimit,
  (req, res) => leaveAppealController.teacherApproveLeaveRequest(req, res),
);

router.put(
  "/teacher/leave-requests/:id/reject",
  authenticate,
  requireRole(["teacher"]),
  generalRateLimit,
  (req, res) => leaveAppealController.teacherRejectLeaveRequest(req, res),
);

router.put(
  "/teacher/leave-requests/:id/appeal/approve",
  authenticate,
  requireRole(["teacher"]),
  generalRateLimit,
  (req, res) => leaveAppealController.teacherApproveLeaveAppeal(req, res),
);

router.put(
  "/teacher/leave-requests/:id/appeal/reject",
  authenticate,
  requireRole(["teacher"]),
  generalRateLimit,
  (req, res) => leaveAppealController.teacherRejectLeaveAppeal(req, res),
);

/**
 * School Leave Request Routes
 */

/**
 * @route   GET /api/v1/school/leave-requests
 * @desc    Get all leave requests for school panel
 * @access  Private (School Admin)
 */
router.get(
  "/school/leave-requests",
  authenticate,
  requireRole(["school"]),
  generalRateLimit,
  (req, res) => leaveController.getSchoolLeaveRequests(req, res),
);

/**
 * @route   GET /api/v1/school/leave-requests/:id
 * @desc    Get a single leave request by ID
 * @access  Private (School Admin)
 */
router.get(
  "/school/leave-requests/:id",
  authenticate,
  requireRole(["school"]),
  generalRateLimit,
  (req, res) => leaveController.getLeaveRequestById(req, res),
);

/**
 * @route   PUT /api/v1/school/leave-requests/:id/approve
 * @desc    Approve a leave request
 * @access  Private (School Admin)
 */
router.put(
  "/school/leave-requests/:id/approve",
  authenticate,
  requireRole(["school"]),
  generalRateLimit,
  (req, res) => leaveController.approveLeaveRequest(req, res),
);

/**
 * @route   PUT /api/v1/school/leave-requests/:id/reject
 * @desc    Reject a leave request
 * @access  Private (School Admin)
 */
router.put(
  "/school/leave-requests/:id/reject",
  authenticate,
  requireRole(["school"]),
  generalRateLimit,
  (req, res) => leaveController.rejectLeaveRequest(req, res),
);

router.put(
  "/school/leave-requests/:id/appeal/approve",
  authenticate,
  requireRole(["school"]),
  generalRateLimit,
  (req, res) => leaveAppealController.schoolApproveLeaveAppeal(req, res),
);

router.put(
  "/school/leave-requests/:id/appeal/reject",
  authenticate,
  requireRole(["school"]),
  generalRateLimit,
  (req, res) => leaveAppealController.schoolRejectLeaveAppeal(req, res),
);

/**
 * @route   GET /api/v1/school/leave-requests/statistics
 * @desc    Get leave request statistics
 * @access  Private (School Admin)
 */
router.get(
  "/school/leave-requests/statistics",
  authenticate,
  requireRole(["school"]),
  generalRateLimit,
  (req, res) => leaveController.getLeaveRequestStatistics(req, res),
);

export default router;
